/**
 * The grant handoff between "I placed/answered a call" and the room that plays it.
 *
 * `POST /calls` and `POST /calls/:id/answer` both mint a media grant, and both
 * are single-use — answering twice is an error by construction. Navigation
 * therefore has to CARRY the grant rather than re-request it, which is what this
 * one-slot store is for. A reload empties it and the room falls back to
 * `POST /calls/:id/join`, so nothing here is load-bearing for correctness.
 */
import type { CallCredentials, CallKind } from '@cyphex/types/calls';
import { createStore } from '@/lib/tinyStore';

export interface ActiveCallHandoff {
  readonly callId: string;
  readonly credentials: CallCredentials;
  readonly kind: CallKind;
  readonly direction: 'incoming' | 'outgoing';
  /** Who the other side is, so the room has a name before the SFU roster lands. */
  readonly peerName: string;
  /** The other party's user id, for "Call again" off the end screen. */
  readonly peerId: string | null;
  /** The callee may answer a video call with the camera off. */
  readonly withVideo: boolean;
  /** Outgoing calls open on a ringing screen until `call.connected` arrives. */
  readonly connected: boolean;
}

export const activeCallStore = createStore<ActiveCallHandoff | null>(null);

export const activeCallActions = {
  set(handoff: ActiveCallHandoff): void {
    activeCallStore.set(handoff);
  },

  /** The callee picked up — the caller's ringing screen becomes a live room. */
  markConnected(callId: string): void {
    activeCallStore.set((current) =>
      current === null || current.callId !== callId || current.connected
        ? current
        : { ...current, connected: true },
    );
  },

  clear(callId?: string): void {
    activeCallStore.set((current) =>
      current === null || (callId !== undefined && current.callId !== callId) ? current : null,
    );
  },
};

export const selectHandoff = (state: ActiveCallHandoff | null): ActiveCallHandoff | null => state;
